import React, { useEffect, useState } from 'react';
import { Card, Radio, RadioChangeEvent, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import FirstFirst from '@src/icons/flow/first-first.svg';
import LastFirst from '@src/icons/flow/last-first.svg';

type Option = 'first_first' | 'last_first';

interface Props {
  isView?: boolean;
  value?: Option;
  onChange?: (val: Option) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  padding-left: 29px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  gap: 16px;
  .emp-ant-radio {
    display: none;
  }
  .emp-ant-radio-wrapper span.emp-ant-radio + * {
    padding-inline-start: 0;
  }
`;

const StyledCard = styled.div<{ selected: boolean }>`
  width: 230px;
  border-radius: 8px;
  padding: 4px 8px 4px 16px;
  transition: border-color 0.3s;
  border: 1px solid ${(props) => (props.selected ? '#0046FF' : '#D9D9D9')};
  background-color: ${(props) => (props.selected ? '#F2F6FF' : '#fff')};
  .title {
    font-weight: ${(props) => (props.selected ? 500 : 400)};
    > span {
      font-weight: 500;
    }
  }
  .emp-ant-card {
    .emp-ant-card-body {
      padding: 0;
    }
  }

  .image {
    width: calc(100% + 29px);
    display: block;
    height: 28px;
    margin-left: -27px;
  }
`;

export default function PairingLogicSelector({ isView, value, onChange }: Props) {
  const [internalValue, setInternalValue] = useState(value || 'first_first');

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const handleChange = (e: RadioChangeEvent) => {
    setInternalValue(e.target.value);
    onChange?.(e.target.value as Option);
  };
  const FirstFirstCard = (
    <StyledCard selected={isView ? false : internalValue === 'first_first'}>
      <span className="title">
        Use the <span>first</span> initial event
      </span>
      <Tooltip
        overlayStyle={{ maxWidth: 400 }}
        title={
          "First Initial Event: ​​ Tracks from the first attempt, treating any following initial events as retries with frustrations. Best for full journey friction analysis (e.g., 'Payment start → success') when the first initial event is the start of the measured experience."
        }
      >
        <InfoCircleOutlined style={{ marginLeft: 6 }} />
      </Tooltip>
      <FirstFirst className="image" />
    </StyledCard>
  );
  const LastFirstCard = (
    <StyledCard selected={isView ? false : internalValue === 'last_first'}>
      <span className="title">
        Use the <span>last</span> initial event
      </span>
      <Tooltip
        overlayStyle={{ maxWidth: 400 }}
        title={
          "Last Initial Event: ​​ Tracks from the final attempt immediately before conversion, treating prior initial events as explorations. Best for Finalized decision analysis (e.g., 'Cart → Payment' after multiple cart updates) when the last initial event is the start of the measured experience."
        }
      >
        <InfoCircleOutlined style={{ marginLeft: 6 }} />
      </Tooltip>
      <LastFirst className="image" />
    </StyledCard>
  );
  return (
    <Container>
      <Label>How to attribute conversions with multiple initial events?</Label>
      {isView ? (
        internalValue === 'first_first' ? (
          FirstFirstCard
        ) : (
          LastFirstCard
        )
      ) : (
        <StyledRadioGroup
          onChange={handleChange}
          value={internalValue}
          options={[
            {
              label: FirstFirstCard,
              value: 'first_first',
            },
            {
              label: LastFirstCard,
              value: 'last_first',
            },
          ]}
        />
      )}
    </Container>
  );
}
